import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import UserDashboardLayout from "../../layouts/UserDashboardLayout";
import {
  FiBell, FiBellOff, FiAward, FiVideo, FiClock,
  FiSettings, FiCheck, FiTrash2, FiX, FiArrowRight,
  FiDroplet, FiShield, FiRadio, FiFeather, FiCalendar,
  FiList,} from "react-icons/fi";
import Topbar from "../../components/userDashboard/Topbar";
import BrandLoader from "../../components/BrandLoader";
import { GiLotus } from "react-icons/gi";
import { notificationAPI } from "../../services/api";
import { toast } from "react-toastify";

// ─── TOKENS ──────────────────────────────────────────────────────────────────
const T = {
  orange: "#F29A0C", orange2: "#E97818",
  olive: "#6B8A5A",  olive2: "#8BA47A",
  dark: "#1F1F1F",   mid: "#555",
  muted: "#999",     light: "#CCC",
  border: "rgba(239,229,216,0.8)",
};

const glass = {
  background: "rgba(255,255,255,0.82)",
  border: "1px solid rgba(255,255,255,0.95)",
  backdropFilter: "blur(32px)",
  WebkitBackdropFilter: "blur(32px)",
  boxShadow: "0 20px 60px rgba(0,0,0,0.06), inset 0 1px 0 #fff",
};

const INITIAL_NOTIFICATIONS = [];

const CATS = [
  { id: "all",         label: "All",          icon: FiList    },
  { id: "session",     label: "Sessions",     icon: FiRadio   },
  { id: "video",       label: "Videos",       icon: FiVideo   },
  { id: "achievement", label: "Achievements", icon: FiAward   },
  { id: "diet",        label: "Diet",         icon: FiDroplet },
  { id: "system",      label: "System",       icon: FiSettings },
];

const DAYS = ["Today", "Yesterday", "Earlier"];

// ─── NOTIFICATION ITEM ────────────────────────────────────────────────────────
function NotifItem({ notif, onDismiss, onRead }) {
  const Icon = notif.icon;
  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: -12 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 60, scale: 0.95 }}
      transition={{ duration: 0.35, ease: [0.25, 1, 0.5, 1] }}
      onClick={() => onRead(notif)}
      whileHover={{ x: 5, boxShadow: "0 12px 40px rgba(0,0,0,0.09)" }}
      className="relative flex items-start gap-3.5 px-5 py-4 rounded-[20px] mb-2 cursor-pointer overflow-hidden"
      style={{
        background: notif.unread ? "rgba(255,255,255,0.88)" : "rgba(255,255,255,0.5)",
        border: notif.unread ? "1px solid rgba(255,255,255,0.95)" : `1px solid rgba(239,229,216,0.5)`,
        boxShadow: notif.unread ? "0 8px 30px rgba(0,0,0,0.06)" : "none",
        transition: "box-shadow 0.28s",
      }}>

      {/* Left accent bar for unread */}
      {notif.unread && (
        <div className="absolute left-0 top-0 bottom-0 w-[3px] rounded-r-[2px]"
          style={{ background: `linear-gradient(180deg,${notif.dotColor},${notif.dotColor}88)` }} />
      )}

      {/* Shimmer on hover */}
      <motion.div className="absolute inset-0 pointer-events-none"
        style={{ background: "linear-gradient(90deg,transparent,rgba(255,255,255,0.35),transparent)", x: "-120%" }}
        whileHover={{ x: "120%" }} transition={{ duration: 0.6 }} />

      {/* Icon */}
      <div className="w-11 h-11 rounded-[14px] flex items-center justify-center flex-shrink-0 relative"
        style={{ background: notif.iconBg, boxShadow: notif.iconGlow ? `0 6px 18px ${notif.iconGlow}` : "none" }}>
        <Icon size={18} style={{ color: "#fff" }} />
        {notif.unread && notif.dotColor && (
          <motion.div
            animate={{ scale: [1, 1.4, 1] }} transition={{ duration: 2, repeat: Infinity }}
            className="absolute -top-[3px] -right-[3px] w-2.5 h-2.5 rounded-full"
            style={{ background: notif.dotColor, border: "2px solid #FFF8EE" }} />
        )}
      </div>

      {/* Body */}
      <div className="flex-1 min-w-0">
        <p className="text-[14px] leading-[1.35] mb-1"
          style={{ fontWeight: notif.unread ? 700 : 600, color: notif.unread ? T.dark : "#666" }}>
          {notif.title}
        </p>
        <p className="text-[12px] leading-[1.55]" style={{ color: T.muted }}>{notif.sub}</p>
        {notif.action && (
          <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
            onClick={e => { e.stopPropagation(); onRead(notif); }}
            className="mt-2.5 h-[30px] px-3.5 rounded-full inline-flex items-center gap-1.5 text-[11px] font-bold"
            style={{ background: `${notif.actionColor}18`, border: `1px solid ${notif.actionColor}35`,
              color: notif.actionColor, cursor: "pointer", fontFamily: "'Inter',sans-serif",
              transition: "background 0.2s" }}>
            {notif.action} <FiArrowRight size={11} />
          </motion.button>
        )}
      </div>

      {/* Time */}
      <span className="text-[11px] whitespace-nowrap flex-shrink-0 mt-0.5 ml-2" style={{ color: T.light }}>
        {notif.time}
      </span>

      {/* Dismiss button   visible on hover */}
      <motion.button
        initial={{ opacity: 0 }} whileHover={{ opacity: 1 }}
        onClick={e => { e.stopPropagation(); onDismiss(notif.id); }}
        whileParent={{ opacity: 1 }}
        className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ background: "rgba(200,200,200,0.15)", border: "none", cursor: "pointer" }}
        aria-label="Dismiss">
        <FiX size={12} style={{ color: T.muted }} />
      </motion.button>
    </motion.div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export default function NotificationsPage() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [activeFilter, setActiveFilter]   = useState("all");
  const [unreadOnly, setUnreadOnly]       = useState(false);
  const [loading, setLoading]             = useState(true);
  const [selectedNotif, setSelectedNotif] = useState(null);

  const mapNotification = (n) => {
    const titleLower = (n.title || "").toLowerCase();
    const msgLower   = (n.message || "").toLowerCase();
    const audLower   = (n.audience || "").toLowerCase();
    const catLower   = (n.category || "").toLowerCase();
    const fullText   = `${titleLower} ${msgLower} ${audLower} ${catLower}`;

    let icon = FiSettings;
    let iconBg = "linear-gradient(135deg,#6366f1,#4f46e5)";
    let dotColor = "#6366f1";
    let cat = "system";

    // 1. Achievements & Badges
    if (
      catLower === "achievement" || audLower === "achievement" ||
      fullText.includes("achievement") || fullText.includes("badge") ||
      fullText.includes("streak") || fullText.includes("century") ||
      fullText.includes("certificate") || fullText.includes("unlocked") ||
      fullText.includes("yogi") || fullText.includes("milestone")
    ) {
      icon = FiAward;
      iconBg = `linear-gradient(135deg,${T.orange},${T.orange2})`;
      dotColor = T.orange;
      cat = "achievement";
    }
    // 2. Videos & Library
    else if (
      catLower === "video" ||
      fullText.includes("video") || fullText.includes("uploaded") ||
      fullText.includes("library") || fullText.includes("practice video") ||
      fullText.includes("training library")
    ) {
      icon = FiVideo;
      iconBg = "linear-gradient(135deg,#a855f7,#7c3aed)";
      dotColor = "#a855f7";
      cat = "video";
    }
    // 3. Diet & Nutrition
    else if (
      catLower === "diet" ||
      fullText.includes("diet") || fullText.includes("salad") ||
      fullText.includes("meal") || fullText.includes("nutrition") ||
      fullText.includes("recipe") || fullText.includes("calorie") ||
      fullText.includes("fasting") || fullText.includes("hydration")
    ) {
      icon = FiDroplet;
      iconBg = `linear-gradient(135deg,${T.olive},${T.olive2})`;
      dotColor = T.olive;
      cat = "diet";
    }
    // 4. Subscriptions, Plans & System (Explicit check so subscription never leaks to session)
    else if (
      catLower === "subscription" || catLower === "system" ||
      fullText.includes("subscription") || fullText.includes("plan") ||
      fullText.includes("membership") || fullText.includes("package") ||
      fullText.includes("upgrade") || fullText.includes("renew") ||
      fullText.includes("extend") || fullText.includes("refund") ||
      fullText.includes("cancel") || fullText.includes("payment") ||
      fullText.includes("invoice") || fullText.includes("tier") ||
      fullText.includes("trial") || fullText.includes("system") ||
      fullText.includes("account") || fullText.includes("profile") ||
      fullText.includes("password") || fullText.includes("security")
    ) {
      icon = FiSettings;
      iconBg = "linear-gradient(135deg,#6366f1,#4f46e5)";
      dotColor = "#6366f1";
      cat = "system";
    }
    // 5. Live Sessions & Classes
    else if (
      catLower === "session" || catLower === "livesession" ||
      fullText.includes("session") || fullText.includes("live class") ||
      fullText.includes("starting in") || fullText.includes("zoom") ||
      fullText.includes("instructor") || fullText.includes("workshop") ||
      fullText.includes("studio")
    ) {
      icon = FiRadio;
      iconBg = "linear-gradient(135deg,#ef4444,#dc2626)";
      dotColor = "#ef4444";
      cat = "session";
    }
    // 6. Default Fallback -> System
    else {
      icon = FiSettings;
      iconBg = "linear-gradient(135deg,#6366f1,#4f46e5)";
      dotColor = "#6366f1";
      cat = "system";
    }

    const d = new Date(n.createdAt);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    let dayGroup = "Earlier";
    if (d.toDateString() === today.toDateString()) {
      dayGroup = "Today";
    } else if (d.toDateString() === yesterday.toDateString()) {
      dayGroup = "Yesterday";
    }
    
    return {
      id: n.id,
      cat: cat,
      unread: !n.isRead,
      day: dayGroup,
      icon: icon,
      iconBg: iconBg,
      iconGlow: "rgba(242,154,12,0.15)",
      dotColor: dotColor,
      title: n.title,
      sub: n.message,
      time: d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }),
      action: "Read Now",
      actionColor: dotColor
    };
  };

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const res = await notificationAPI.getNotifications();
      if (res.data?.success) {
        const mappedList = (res.data.data || []).map(mapNotification);
        setNotifications(mappedList);

        // Web Push Browser Notification trigger for unread achievement notifications
        if (typeof window !== "undefined" && "Notification" in window) {
          if (window.Notification.permission === "default") {
            window.Notification.requestPermission();
          }
          if (window.Notification.permission === "granted") {
            const unreadAchieve = mappedList.find(n => n.unread && n.cat === "achievement");
            if (unreadAchieve) {
              new window.Notification(unreadAchieve.title, {
                body: unreadAchieve.sub,
                icon: "/assets/logo.jpeg"
              });
            }
          }
        }
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load notifications.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const unreadCount = notifications.filter(n => n.unread).length;
  const totalCount  = notifications.length;

  const filtered = notifications.filter(n => {
    const fc = activeFilter === "all" || n.cat === activeFilter;
    const fu = !unreadOnly || n.unread;
    return fc && fu;
  });

  const catCount = (cat) => notifications.filter(n => cat === "all" || n.cat === cat).length;

  const markAllRead = async () => {
    const unread = notifications.filter(n => n.unread);
    if (!unread.length) return;
    try {
      await notificationAPI.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, unread: false })));
      toast.success("All notifications marked as read!");
    } catch (err) {
      console.error("Failed to mark all as read:", err);
      setNotifications(prev => prev.map(n => ({ ...n, unread: false })));
    }
  };

  const clearAll = async () => {
    if (!notifications.length) return;
    try {
      await notificationAPI.clearAllNotifications();
      setNotifications([]);
      toast.success("All notifications cleared successfully!");
    } catch (err) {
      console.error("Failed to clear notifications on backend:", err);
      setNotifications([]);
      toast.success("All notifications cleared.");
    }
  };

  const dismiss = async (id) => {
    try {
      setNotifications(prev => prev.filter(n => n.id !== id));
      await notificationAPI.deleteNotification(id);
    } catch (err) {
      console.error("Failed to dismiss notification:", err);
    }
  };
  
  const markRead = async (notif) => {
    try {
      if (notif.unread) {
        await notificationAPI.markAsRead(notif.id);
        setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, unread: false } : n));
      }
      setSelectedNotif(notif);
    } catch (err) {
      console.error("Failed to mark notification as read:", err);
    }
  };

  return (
    <UserDashboardLayout>
      <div style={{ fontFamily: "'Inter',sans-serif" }}>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@700;800;900&family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet" />

        {/* Ambient orbs */}
        <motion.div animate={{ y:[0,-20,0], x:[0,12,0] }} transition={{ duration:13, repeat:Infinity, ease:"easeInOut" }}
          className="fixed w-[440px] h-[440px] rounded-full pointer-events-none"
          style={{ background:"radial-gradient(circle,rgba(242,154,12,0.08),transparent 70%)", filter:"blur(80px)", top:-140, right:-90 }} />
        <motion.div animate={{ y:[0,14,0], x:[0,-10,0] }} transition={{ duration:16, repeat:Infinity, ease:"easeInOut" }}
          className="fixed w-[360px] h-[360px] rounded-full pointer-events-none"
          style={{ background:"radial-gradient(circle,rgba(107,138,90,0.07),transparent 70%)", filter:"blur(80px)", bottom:-80, left:-80 }} />

        <div className="relative z-10 w-full min-w-0">
            <Topbar/>
          {/* ── HERO ── */}
          <motion.div initial={{ opacity:0, y:24 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.7 }}
            className="nt-hero relative overflow-hidden rounded-[36px] p-9 mb-5" style={glass}>
            <div className="absolute inset-0 pointer-events-none opacity-[0.03] rounded-[inherit]"
              style={{ backgroundImage:"radial-gradient(circle,#000 1px,transparent 0)", backgroundSize:"22px 22px" }} />
            <div className="absolute top-0 right-0 pointer-events-none"
              style={{ width:260, height:260, background:"radial-gradient(circle,rgba(242,154,12,0.09),transparent)", filter:"blur(40px)" }} />

            <div className="nt-hero-row flex items-center justify-between gap-8 relative">
              <div className="nt-hero-text">
                <div className="nt-hero-head flex items-center gap-3 mb-4">
                  <motion.div animate={{ rotate:[0,12,-10,6,-4,0] }} transition={{ duration:3, repeat:Infinity, repeatDelay:4 }}
                    className="w-11 h-11 rounded-[16px] flex items-center justify-center"
                    style={{ background:`linear-gradient(135deg,${T.orange},${T.orange2})`, boxShadow:`0 8px 22px rgba(242,154,12,0.42)` }}>
                    <FiBell size={20} style={{ color:"#fff" }} />
                  </motion.div>
                  <div className="nt-hero-headtext">
                    <p className="text-[11px] font-bold uppercase tracking-[1.5px]" style={{ color:T.orange }}>Notifications</p>
                    <p className="text-[12px]" style={{ color:T.light }}>Stay up to date with your wellness journey</p>
                  </div>
                  {unreadCount > 0 && (
                    <motion.div animate={{ opacity:[1,0.5,1] }} transition={{ duration:2, repeat:Infinity }}
                      className="nt-hero-unread ml-2 flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[11px] font-bold"
                      style={{ background:"rgba(242,154,12,0.1)", border:"1px solid rgba(242,154,12,0.2)", color:"#C8660A" }}>
                      <div className="w-1.5 h-1.5 rounded-full" style={{ background:T.orange }} />
                      {unreadCount} unread
                    </motion.div>
                  )}
                </div>

                <h1 className="nt-hero-title font-black mb-2"
                  style={{ fontFamily:"'Poppins',sans-serif", fontSize:34, letterSpacing:"-1.5px", lineHeight:1.06, color:T.dark }}>
                  Your updates,{" "}
                  <span style={{ background:`linear-gradient(135deg,${T.orange},${T.orange2})`, WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>
                    all in one place.
                  </span>
                </h1>
                <p className="nt-hero-desc text-[14px] leading-[1.8]" style={{ color:T.muted, maxWidth:500 }}>
                  Sessions, achievements, diet plans, and system alerts. Never miss a moment on your journey.
                </p>
                <div className="nt-hero-note inline-flex items-center gap-2 mt-3 px-3.5 py-1.5 rounded-full"
                  style={{ background:"rgba(107,138,90,0.08)", border:"1px solid rgba(107,138,90,0.18)" }}>
                  <FiClock size={12} className="shrink-0" style={{ color:T.olive }} />
                  <span className="text-[11px] font-semibold" style={{ color:T.olive }}>
                    Notifications are kept for 21 days, then removed automatically
                  </span>
                </div>
              </div>

              <div className="nt-hero-actions flex flex-col gap-2 flex-shrink-0">
                <motion.button whileHover={{ y:-2 }} whileTap={{ scale:0.97 }} onClick={markAllRead}
                  className="h-10 px-5 rounded-full text-white text-[12px] font-bold flex items-center gap-2"
                  style={{ background:`linear-gradient(135deg,${T.orange},${T.orange2})`, boxShadow:`0 6px 18px rgba(242,154,12,0.4)`, border:"none", cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                  <FiCheck size={13} /> Mark all read
                </motion.button>
                <motion.button whileHover={{ y:-1 }} onClick={clearAll}
                  className="h-9 px-4 rounded-full text-[12px] font-semibold flex items-center gap-2"
                  style={{ background:"rgba(255,255,255,0.9)", border:`1px solid ${T.border}`, color:T.muted, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                  <FiTrash2 size={12} /> Clear all
                </motion.button>
              </div>
            </div>
          </motion.div>

          {/* ── WEB PUSH NOTIFICATION BANNER (ADDITIVE) ── */}
          <motion.div initial={{ opacity:0, y:12 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.5 }}
            className="rounded-[24px] p-6 mb-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
            style={{ background: "linear-gradient(135deg, rgba(242,154,12,0.1), rgba(107,138,90,0.1))", border: "1.5px solid rgba(242,154,12,0.25)" }}>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-[14px] flex items-center justify-center flex-shrink-0"
                style={{ background: `linear-gradient(135deg, ${T.orange}, ${T.orange2})`, color: "#fff", boxShadow: "0 4px 14px rgba(242,154,12,0.35)" }}>
                <FiBell size={18} />
              </div>
              <div>
                <p className="font-bold text-[15px] text-[#1F1F1F]">Browser & Closed-Tab Web Push Alerts</p>
                <p className="text-[12px] text-[#777] mt-0.5">Receive native system notifications on Windows, Android & Mac even when your browser tab is closed.</p>
              </div>
            </div>

            <div className="flex items-center gap-2.5 flex-wrap flex-shrink-0">
              <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.96 }}
                onClick={async () => {
                  try {
                    const { enableBrowserPushNotifications } = await import("../../services/pushNotification");
                    await enableBrowserPushNotifications();
                    toast.success("✓ Browser Push Notifications Enabled! You will get alerts even when app is closed.");
                  } catch (e) {
                    toast.error(e.message || "Could not enable push notifications.");
                  }
                }}
                className="h-10 px-6 rounded-full text-white text-[13px] font-bold flex items-center gap-2"
                style={{ background: `linear-gradient(135deg, ${T.olive}, ${T.olive2})`, border: "none", cursor: "pointer", boxShadow: "0 4px 14px rgba(107,138,90,0.3)" }}>
                <FiCheck size={14} /> Enable Web Push Notifications
              </motion.button>
            </div>
          </motion.div>

          {/* ── STATS BAND ── */}
          {(() => {
            const todayNotifs = notifications.filter(n => n.day === "Today");
            const sessionsToday = todayNotifs.filter(n => n.cat === "session").length;
            const achievementsToday = todayNotifs.filter(n => n.cat === "achievement").length;
            const videosToday = todayNotifs.filter(n => n.cat === "video").length;

            return (
              <motion.div initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.1 }}
                className="ud-kpi grid grid-cols-4 mb-5 rounded-[24px] overflow-hidden"
                style={{ border:"1px solid rgba(255,255,255,0.9)", boxShadow:"0 12px 40px rgba(0,0,0,0.05)" }}>
                {[
                  { icon:FiBell,     val:unreadCount,  label:"Unread Updates",  accent:T.orange, bg:"linear-gradient(145deg,#FFF4E8,#FFF8EE)" },
                  { icon:FiRadio,    val:sessionsToday, label:"Sessions Today",   accent:T.olive,  bg:"linear-gradient(145deg,#F5FAF2,#F9FDF5)" },
                  { icon:FiAward,    val:achievementsToday, label:"Achievements Today", accent:T.orange, bg:"linear-gradient(145deg,#FFF4E8,#FFF8EE)" },
                  { icon:FiVideo,    val:videosToday,  label:"Videos Uploaded Today",accent:T.olive, bg:"linear-gradient(145deg,#F5FAF2,#F9FDF5)" },
                ].map((s, i) => (
                  <motion.div key={s.label} whileHover={{ background:"rgba(255,255,255,0.95)" }}
                    className="relative overflow-hidden px-5 py-4 flex items-center gap-3"
                    style={{ background:s.bg, borderRight:i<3?`1px solid rgba(239,229,216,0.5)`:"none" }}>
                    <div className="absolute bottom-0 left-0 right-0 h-[2px]"
                      style={{ background:`linear-gradient(90deg,${s.accent},${s.accent}88)` }} />
                    <div className="w-9 h-9 rounded-[12px] flex items-center justify-center flex-shrink-0"
                      style={{ background:`linear-gradient(135deg,${s.accent},${s.accent}CC)`, boxShadow:`0 5px 14px ${s.accent}40` }}>
                      <s.icon size={16} style={{ color:"#fff" }} />
                    </div>
                    <div>
                      <p className="ud-kpi-val font-black text-[#1F1F1F] leading-none"
                        style={{ fontFamily:"'Poppins',sans-serif", fontSize:24, letterSpacing:"-1px" }}>{s.val}</p>
                      <p className="ud-kpi-lbl text-[10px] font-bold uppercase tracking-[0.7px] mt-0.5" style={{ color:T.light }}>{s.label}</p>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            );
          })()}

          {/* ── FILTER + TOGGLE ROW ── */}
          <motion.div initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.14 }}
            className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <div className="ud-scroll flex gap-2 flex-wrap">
              {CATS.map(cat => {
                const active = activeFilter === cat.id;
                const Icon = cat.icon;
                return (
                  <motion.button key={cat.id} onClick={() => setActiveFilter(cat.id)} whileTap={{ scale:0.94 }}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-full text-[12px] font-bold border-0 outline-none cursor-pointer"
                    style={active
                      ? { background:`linear-gradient(135deg,${T.orange},${T.orange2})`, color:"#fff", boxShadow:`0 6px 18px rgba(242,154,12,0.38)`, fontFamily:"'Inter',sans-serif", transform:"translateY(-1px)" }
                      : { background:"rgba(255,255,255,0.82)", color:T.muted, border:`1px solid ${T.border}`, fontFamily:"'Inter',sans-serif" }
                    }>
                    <Icon size={12} />
                    {cat.label}
                    <span className="px-1.5 py-0.5 rounded-full text-[10px] font-black"
                      style={active
                        ? { background:"rgba(255,255,255,0.25)" }
                        : { background:"rgba(242,154,12,0.1)", color:"#C8660A" }
                      }>{catCount(cat.id)}</span>
                  </motion.button>
                );
              })}
            </div>

            <motion.button whileTap={{ scale:0.95 }} onClick={() => setUnreadOnly(!unreadOnly)}
              className="flex items-center gap-2 h-9 px-4 rounded-full text-[12px] font-semibold border-0 outline-none cursor-pointer"
              style={unreadOnly
                ? { background:`linear-gradient(135deg,${T.orange},${T.orange2})`, color:"#fff", boxShadow:`0 5px 14px rgba(242,154,12,0.38)`, fontFamily:"'Inter',sans-serif" }
                : { background:"rgba(255,255,255,0.82)", color:T.muted, border:`1px solid ${T.border}`, fontFamily:"'Inter',sans-serif" }
              }>
              <FiBell size={13} /> Unread only
            </motion.button>
          </motion.div>

          {/* ── NOTIFICATION LIST ── */}
          <AnimatePresence mode="popLayout">
            {loading ? (
              <BrandLoader 
                message="Loading Notifications..." 
                subMessage="Syncing your updates and alerts" 
                minHeight="min-h-[40vh]" 
              />
            ) : filtered.length === 0 ? (
              <motion.div key="empty" initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0 }}
                className="text-center py-16">
                <div className="w-[72px] h-[72px] rounded-full flex items-center justify-center mx-auto mb-5"
                  style={{ background:"linear-gradient(135deg,rgba(242,154,12,0.12),rgba(233,120,24,0.08))" }}>
                  <FiBellOff size={30} style={{ color:T.orange2 }} />
                </div>
                <p className="font-black text-[18px] text-[#1F1F1F] mb-2"
                  style={{ fontFamily:"'Poppins',sans-serif" }}>No notifications</p>
                <p className="text-[13px]" style={{ color:T.muted }}>
                  {unreadOnly ? "No unread notifications, you're all caught up!" : "You're all caught up! Check back later."}
                </p>
              </motion.div>
            ) : (
              DAYS.map(day => {
                const dayItems = filtered.filter(n => n.day === day);
                if (!dayItems.length) return null;
                return (
                  <div key={day}>
                    <div className="flex items-center gap-3 mb-3 mt-5 first:mt-0">
                      <span className="text-[11px] font-black uppercase tracking-[1.2px]" style={{ color:T.light }}>{day}</span>
                      <span className="text-[10px]" style={{ color:"#DDD" }}>({dayItems.length})</span>
                      <div className="flex-1 h-px" style={{ background:"rgba(239,229,216,0.6)" }} />
                    </div>
                    <AnimatePresence initial={false}>
                      {dayItems.map((notif, i) => (
                        <NotifItem key={notif.id} notif={notif}
                          onDismiss={dismiss} onRead={markRead} />
                      ))}
                    </AnimatePresence>
                  </div>
                );
              })
            )}
          </AnimatePresence>

        </div>
      </div>

      {/* Dynamic details modal */}
      <AnimatePresence>
        {selectedNotif && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            {/* Overlay */}
            <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
              onClick={() => setSelectedNotif(null)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
            
            {/* Modal Container */}
            <motion.div initial={{ scale:0.95, opacity:0, y:20 }} animate={{ scale:1, opacity:1, y:0 }} exit={{ scale:0.95, opacity:0, y:20 }}
              className="bg-white rounded-[28px] max-w-[480px] w-full p-6 relative overflow-hidden shadow-[0_24px_50px_-12px_rgba(0,0,0,0.35)]"
              style={{ border: "1px solid rgba(255,255,255,0.7)", fontFamily: "'Inter',sans-serif" }}>
              
              {/* Top category indicator */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-[14px] flex items-center justify-center text-white"
                  style={{ background: selectedNotif.iconBg }}>
                  <selectedNotif.icon size={18} />
                </div>
                <div>
                  <span className="text-[10px] font-black uppercase tracking-[1.5px] block" style={{ color: selectedNotif.dotColor }}>
                    {selectedNotif.cat}
                  </span>
                  <span className="text-[10px] text-gray-400 block">{selectedNotif.time}</span>
                </div>
              </div>

              {/* Title & Body */}
              <h3 className="font-bold text-[18px] mb-2.5 text-gray-900 leading-tight" style={{ fontFamily: "'Poppins', sans-serif" }}>
                {selectedNotif.title}
              </h3>
              <p className="text-[13px] text-gray-600 leading-relaxed mb-6 whitespace-pre-wrap">
                {selectedNotif.sub}
              </p>

              {/* Buttons */}
              <div className="flex gap-2.5">
                <motion.button whileHover={{ scale:1.02 }} whileTap={{ scale:0.98 }}
                  onClick={() => setSelectedNotif(null)}
                  className="flex-1 py-3 rounded-[16px] text-[12px] font-semibold cursor-pointer border border-solid border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100 transition-colors"
                  style={{ fontFamily: "'Inter', sans-serif" }}>
                  Close
                </motion.button>
                <motion.button whileHover={{ scale:1.02 }} whileTap={{ scale:0.98 }}
                  onClick={() => {
                    const cat = selectedNotif.cat;
                    const text = `${selectedNotif.title || ""} ${selectedNotif.sub || ""}`.toLowerCase();
                    setSelectedNotif(null);
                    if (cat === "session") {
                      navigate("/user/livesession");
                    } else if (cat === "diet") {
                      navigate("/user/dietplan");
                    } else if (cat === "achievement") {
                      navigate("/user/achievements");
                    } else if (cat === "video") {
                      navigate("/user/traininglibrary");
                    } else if (cat === "system") {
                      if (text.includes("subscri") || text.includes("plan") || text.includes("member") || text.includes("package") || text.includes("tier")) {
                        navigate("/user/profile?tab=membership");
                      } else {
                        navigate("/user/settings");
                      }
                    } else {
                      navigate("/user/dashboard");
                    }
                  }}
                  className="flex-1 py-3 rounded-[16px] text-[12px] font-bold cursor-pointer text-white border-0 shadow-md"
                  style={{ background: `linear-gradient(135deg, ${T.orange}, ${T.orange2})`, fontFamily: "'Inter', sans-serif" }}>
                  {selectedNotif.cat === "session" ? "Join Session" 
                   : selectedNotif.cat === "video" ? "Watch Video"
                   : selectedNotif.cat === "diet" ? "View Diet Plan"
                   : selectedNotif.cat === "achievement" ? "Check Achievements"
                   : `${selectedNotif.title || ""} ${selectedNotif.sub || ""}`.toLowerCase().includes("subscri") || `${selectedNotif.title || ""} ${selectedNotif.sub || ""}`.toLowerCase().includes("plan") ? "View Membership"
                   : "View Settings"}
                </motion.button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </UserDashboardLayout>
  );
}